import { AgentReply } from 'app/shared/models/agent-reply.model';

/**
 * The canned "why" answer each agent gives, transcribed from the SOA Design Kit prototype.
 *
 * Read only by `DeoAgentReplyService.fetchAgentReply`, which is the seam the endpoint replaces — so
 * this file is deleted with that method and nothing else has to change. Keyed by
 * `AgentDefinition.id`, the same ids the alerts carry as `owningAgentId`.
 *
 * `answer` is markdown, because that is what the endpoint will stream: the finding, the contributing
 * factors and the suggested path are all prose in one message, rendered by the widget's own
 * `formatMessageContent` like any other assistant turn. `MessageFormatterService` handles `**bold**`,
 * `- ` lists and `1. ` lists, which is all this needs.
 */
export function buildAgentRepliesFixture(): Record<string, AgentReply> {
  return {
    'service-anomaly': {
      agentId: 'service-anomaly',
      agentName: 'Service Level Anomaly Detection',
      answer: [
        'Achieved fill at DC2 is 91.2% against a 96.0% target — 4.8pp below, tagged gradual. It crossed the 3pp threshold in week 9 and has widened since.',
        '',
        '**Contributing factors**',
        '- Receipt lead time drift — supplier VN-118 — **68%**',
        '- Safety stock not re-planned at observed lead time — **21%**',
        '- Demand variability above plan — 3 SKU families — **11%**',
        '',
        '**Suggested path**',
        '1. Re-plan safety stock on 1,412 SKULs at the observed 34-day lead time. Fixes the cause — closes about 3.2pp of the gap once the plan refreshes.',
        '2. Raise a lead-time review with supplier VN-118 — the planning value is 13 days stale. Stops it recurring. Needs a person; nothing changes until the value is agreed.',
        '3. Expedite the 14 SKULs already below reorder point. Buys time while the re-plan takes effect — cost impact, so worth a decision.'
      ].join('\n'),
      followUps: ['Walk me through step 1', 'What if I leave it alone?', 'Show the 14 SKULs']
    },

    'inventory-health-anomaly': {
      agentId: 'inventory-health-anomaly',
      agentName: 'Inventory Health Anomaly Detection',
      answer: [
        'Days of supply at DC1 is 47 days against a policy band of 32–38 — 22% above the ceiling, tagged sudden. 260 SKULs moved together, which points at a plan change rather than demand.',
        '',
        '**Contributing factors**',
        '- Demand plan revised down, replenishment not re-run — **61%**',
        '- Minimum order quantity rounding on 74 SKULs — **27%**',
        '- Early receipts pulled into the period — **12%**',
        '',
        '**Suggested path**',
        '1. Re-run replenishment on the 260 SKULs against the revised demand plan. Fixes the cause — brings coverage back inside the band without touching policy.',
        '2. Review MOQ on the 74 rounded SKULs with procurement. Structural; needs a person. Worth doing only if the excess persists.',
        '3. Hold the next inbound wave for the 41 SKULs above 60 days. Avoids adding to the excess — cost and supplier impact, so decide explicitly.'
      ].join('\n'),
      followUps: ['Walk me through step 1', 'What if I leave it alone?', 'Show the 41 SKULs above 60 days']
    },

    'supply-performance-anomaly': {
      agentId: 'supply-performance-anomaly',
      agentName: 'Supply Performance Anomaly Detection',
      answer: [
        'Receipt lead time on supplier VN-118 is running at 34 days against a planning value of 21 — 62% above, tagged gradual. The last nine receipts are all late, so this is drift rather than a single miss.',
        '',
        '**Contributing factors**',
        '- Port dwell on inbound lanes from VN — **54%**',
        '- Supplier production lead time extended, not communicated — **31%**',
        '- Order consolidation adding transit days — **15%**',
        '',
        '**Suggested path**',
        '1. Update the planning lead time on VN-118 to the observed 34 days. Fixes the parameter the plan is wrong on — reversible, applies next cycle.',
        '2. Raise a lead-time review with the supplier before the next PO wave. Confirms whether 34 days is the new normal. Needs a person.',
        '3. Re-check service level on the 1,412 SKULs sourced from VN-118. This drift is the largest driver behind the DC2 fill-rate breach.'
      ].join('\n'),
      followUps: ['Which SKULs are exposed?', 'What if I leave it alone?', 'Is DC2 service related?']
    }
  };
}
